/**
 * @module public/js/focusmode/inFlightQueuePolicy
 * @summary Plan independent daily limits for tasks from sequences in flight.
 */

/** Maximum supported daily task limit for one sequence. */
export const MAX_IN_FLIGHT_DAILY_LIMIT = 500;

/** Deferral marker used for tasks moved by an in-flight queue policy. */
export const IN_FLIGHT_QUEUE_MIX_DEFERRED_BY = 'queueMixInFlight';

/**
 * Normalize an in-flight policy read from QueueMixPolicies.
 *
 * @param {Record<string, unknown>|null|undefined} rawPolicy - Candidate policy.
 * @returns {{enabled:boolean,rules:Array<{playbookId:string,dailyMax:number}>}} Safe policy.
 */
export function normalizeInFlightQueuePolicy(rawPolicy = {}) {
  const ids = new Set();
  const rules = [];
  const source = Array.isArray(rawPolicy?.rules) ? rawPolicy.rules : [];
  let invalid = source.length > 25;
  source.slice(0, 25).forEach((rule) => {
    const playbookId = String(rule?.playbookId || '').trim();
    const value = rule?.dailyMax ?? rule?.value;
    const dailyMax = Number(value);
    if (
      !playbookId ||
      ids.has(playbookId) ||
      value === '' ||
      value === null ||
      value === undefined ||
      !Number.isFinite(dailyMax) ||
      dailyMax < 0
    ) {
      invalid = true;
      return;
    }
    ids.add(playbookId);
    rules.push({
      playbookId,
      dailyMax: Math.min(MAX_IN_FLIGHT_DAILY_LIMIT, Math.floor(dailyMax)),
    });
  });
  return {
    enabled: rawPolicy?.enabled === true && rules.length > 0 && !invalid,
    rules,
  };
}

/**
 * Resolve a task's playbook id from supported mirror-task fields.
 *
 * @param {Record<string, unknown>} task - Mirror task payload.
 * @returns {string} Playbook id or an empty string.
 */
export function resolveInFlightTaskPlaybookId(task = {}) {
  return String(
    task.playbookId ||
      task.resolvedPlaybookId ||
      task.metadata?.playbookId ||
      task.metadata?.resolvedPlaybookId ||
      ''
  ).trim();
}

/**
 * Convert a Firestore-like date value to a JavaScript Date.
 *
 * @param {unknown} value - Timestamp, Date, string, or number.
 * @returns {Date|null} Parsed date or null.
 */
function toDate(value) {
  const candidate =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : typeof value?.toMillis === 'function'
        ? new Date(value.toMillis())
        : value instanceof Date
          ? value
          : typeof value === 'string' || typeof value === 'number'
            ? new Date(value)
            : null;
  return candidate instanceof Date && !Number.isNaN(candidate.getTime())
    ? candidate
    : null;
}

/**
 * Return a yyyy-mm-dd key in a seller timezone.
 *
 * @param {unknown} value - Firestore-like date value.
 * @param {string|null|undefined} timeZone - Seller IANA timezone.
 * @returns {string} Date key or an empty string.
 */
function dateKey(value, timeZone) {
  const date = toDate(value);
  if (!date) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const part = (type) =>
      parts.find((entry) => entry.type === type)?.value || '';
    const year = part('year');
    const month = part('month');
    const day = part('day');
    return year && month && day ? `${year}-${month}-${day}` : '';
  } catch {
    return '';
  }
}

/**
 * Determine whether a task belongs to a sequence that was already in flight.
 *
 * First tasks created on the business day are intentionally excluded: their
 * admission is governed only by the independent new-sequence-start policy.
 * Follow-up steps and first steps carried over from an earlier day qualify.
 *
 * @param {Record<string, unknown>} task - Mirror task payload.
 * @param {string} businessDate - Seller-local yyyy-mm-dd.
 * @param {string|null|undefined} timeZone - Seller IANA timezone.
 * @returns {boolean} Whether the in-flight daily limit applies.
 */
export function isTaskAlreadyInFlight(task = {}, businessDate, timeZone) {
  if (!resolveInFlightTaskPlaybookId(task) || !businessDate) return false;
  const rawStepIndex =
    task.stepIdx ??
    task.stepIndex ??
    task.metadata?.idx ??
    task.metadata?.stepIdx ??
    null;
  const stepIndex = Number(rawStepIndex);
  if (Number.isFinite(stepIndex) && stepIndex > 0) return true;

  const createdDate = dateKey(task.createdAt, timeZone);
  if (createdDate) return createdDate < businessDate;

  // A legacy first-step task without creation metadata is not allowed to make
  // the in-flight policy consume a same-day start quota accidentally.
  return Number.isFinite(stepIndex) ? stepIndex > 0 : false;
}

/**
 * Convert a Firestore-like date value to sortable epoch milliseconds.
 *
 * @param {unknown} value - Candidate date value.
 * @returns {number} Epoch milliseconds, or zero when unavailable.
 */
function toEpochMillis(value) {
  return toDate(value)?.getTime() || 0;
}

/**
 * Determine whether cadence metadata makes a task fixed.
 *
 * Provisional CRM mirrors can expose the nested step before the canonical
 * top-level `fixed` flag arrives, so maxLag zero must be treated equivalently.
 *
 * @param {Record<string, unknown>} task - Mirror task payload.
 * @returns {boolean} Whether cadence metadata marks the task as fixed.
 */
export function isEffectiveFixedTask(task = {}) {
  if (task.fixed === true) return true;
  const rawMaxLag =
    task.maxLag ??
    task.step?.maxLag ??
    task.metadata?.maxLag ??
    task.metadata?.step?.maxLag;
  if (rawMaxLag === undefined || rawMaxLag === null || rawMaxLag === '') {
    return false;
  }
  return Number(rawMaxLag) === 0;
}

/**
 * Sort tasks so active and urgent work stays in today's queue first.
 *
 * @param {Record<string, unknown>} left - First task.
 * @param {Record<string, unknown>} right - Second task.
 * @returns {number} Standard array-sort comparison value.
 */
function compareKeepPriority(left = {}, right = {}) {
  const leftInProgress =
    String(left.status || '').toUpperCase() === 'IN_PROGRESS' ? 1 : 0;
  const rightInProgress =
    String(right.status || '').toUpperCase() === 'IN_PROGRESS' ? 1 : 0;
  if (leftInProgress !== rightInProgress) {
    return rightInProgress - leftInProgress;
  }
  const priorityDifference =
    Number(right.priority || 0) - Number(left.priority || 0);
  if (priorityDifference) return priorityDifference;
  const dueDifference =
    toEpochMillis(left.dueAt || left.dueDate) -
    toEpochMillis(right.dueAt || right.dueDate);
  if (dueDifference) return dueDifference;
  const createdDifference =
    toEpochMillis(left.createdAt) - toEpochMillis(right.createdAt);
  if (createdDifference) return createdDifference;
  return String(left.id || '').localeCompare(String(right.id || ''));
}

/**
 * Plan the tasks kept and deferred by independent in-flight daily limits.
 *
 * @param {object} input - Planning input.
 * @param {Array<Record<string, unknown>>} [input.tasks] - Today's task candidates.
 * @param {Record<string, unknown>} [input.policy] - Raw or normalized policy.
 * @param {string} input.businessDate - Seller-local yyyy-mm-dd.
 * @param {string|null|undefined} [input.timeZone] - Seller IANA timezone.
 * @returns {{enabled:boolean,keptTasks:Array<object>,deferredTasks:Array<object>,states:Array<object>}} Daily plan.
 */
export function buildInFlightDailyLimitPlan({
  tasks = [],
  policy = {},
  businessDate = '',
  timeZone = null,
} = {}) {
  const normalized = normalizeInFlightQueuePolicy(policy);
  const taskList = Array.isArray(tasks) ? tasks : [];
  if (!normalized.enabled) {
    return {
      enabled: false,
      keptTasks: taskList,
      deferredTasks: [],
      states: [],
    };
  }

  const deferredIds = new Set();
  const states = normalized.rules.map((rule) => {
    const matching = taskList
      .filter(
        (task) =>
          resolveInFlightTaskPlaybookId(task) === rule.playbookId &&
          isTaskAlreadyInFlight(task, businessDate, timeZone)
      )
      .sort(compareKeepPriority);
    const inProgress = matching.filter(
      (task) => String(task.status || '').toUpperCase() === 'IN_PROGRESS'
    );
    const fixed = matching.filter(
      (task) =>
        isEffectiveFixedTask(task) &&
        String(task.status || '').toUpperCase() !== 'IN_PROGRESS'
    );
    const hardPaused = rule.dailyMax === 0;
    const keptCount = Math.min(matching.length, rule.dailyMax);
    matching.slice(keptCount).forEach((task) => {
      const taskId = String(task.id || '').trim();
      if (taskId) deferredIds.add(taskId);
    });
    return {
      playbookId: rule.playbookId,
      dailyMax: rule.dailyMax,
      hardCap: true,
      hardPaused,
      available: matching.length,
      inProgress: inProgress.length,
      fixed: fixed.length,
      kept: keptCount,
      deferred: Math.max(0, matching.length - keptCount),
    };
  });

  return {
    enabled: true,
    keptTasks: taskList.filter(
      (task) => !deferredIds.has(String(task.id || '').trim())
    ),
    deferredTasks: taskList.filter((task) =>
      deferredIds.has(String(task.id || '').trim())
    ),
    states,
  };
}
